import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { QueryRowType } from "@rocicorp/zero";
import { createContext, useContext as useReactContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { createId } from "@corpuscomplete/core/utils";
import { AgentSession } from "@corpuscomplete/core/ai/agent-session";
import { MealPlanningMemberContextAgent } from "@corpuscomplete/core/nutrition/meal-planning-agent";
import type { MealPlanningSession } from "@corpuscomplete/core/nutrition/meal-planning-session";
import { NutritionProgram } from "@corpuscomplete/core/nutrition/program";
import type { Scope } from "@corpuscomplete/core/auth/scope";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { NutritionProgramWizard } from "../../program/wizard";
import { ActionAvailability } from "../../../common/action-availability";
import { AskQuestions, type AgentTools } from "../../../ai/agent/tools";

export namespace MealPlanningMemberContext {
  export type Context = {
    userId: string;
    mealPlanningSessionId: string;
    dateStart: Temporal.PlainDate;
    dateEnd: Temporal.PlainDate;
    sessionScope: Scope.Info<"user" | "space">;
  };

  const Context = createContext<Context | null>(null);

  type ChatPart = MealPlanningMemberContextAgent.ChatUIMessage["parts"][number];

  type AskQuestionsPart = Extract<ChatPart, { type: "tool-askQuestions" }>;

  type AskQuestionsCompletedPart = Extract<AskQuestionsPart, { state: "output-available" }>;

  type SessionRow = NonNullable<QueryRowType<typeof MealPlanningMemberContextAgent.mySession>>;

  type ResultRow = QueryRowType<typeof MealPlanningMemberContextAgent.results>;

  type Status = "not-started" | "in-progress" | "complete";

  type ReadinessRow = {
    userId: string;
    label: string;
    status: Status;
    result: MealPlanningMemberContextAgent.Result | null;
  };

  type ReadinessSummary = {
    total: number;
    notStarted: number;
    inProgress: number;
    complete: number;
    incompleteMemberNames: string[];
    rows: ReadinessRow[];
  };

  type StartUnavailableReason = MealPlanningMemberContextAgent.CreateUnavailableReason;
  type EditUnavailableReason = MealPlanningMemberContextAgent.EditUnavailableReason;

  type PrerequisiteLifecycle =
    | { type: "planner" }
    | { type: "member-context"; mode: "not-started" | "in-progress" | "complete-waiting" };

  export type Readiness =
    | { status: "ready" }
    | {
        status: "missing-nutrition-program";
        reason: "no-program" | "uncovered-date" | "mixed-program-days";
      };

  export type ReadinessState = { status: "loading" } | Readiness;

  export const saveToolName = "saveMemberContext";

  export const deferredToolPresenter: AgentTools.DeferredToolPresenter = {
    isDeferredToolPart: (part) => isSaveMemberContextPart(part) || isAskQuestionsPart(part),
    getDeferredToolLabel: (part) => {
      if (!part) return undefined;
      if (isAskQuestionsPart(part)) return "Answer questions";
      if (isSaveMemberContextPart(part)) return "Save preferences";
      return undefined;
    },
  };

  export function DataProvider({ children, ...context }: PropsWithChildren<Context>) {
    return <Context value={context}>{children}</Context>;
  }

  export function useContext(): Context {
    const context = useReactContext(Context);
    if (!context) {
      throw new Error("MealPlanningMemberContext.DataProvider is required");
    }
    return context;
  }

  export function useMySession(mealPlanningSessionId: string): SessionRow | undefined {
    const [row] = useQuery(
      queries.mealPlanningAgent.myMemberContextSession({ mealPlanningSessionId }),
    );
    return row;
  }

  export function useResults(mealPlanningSessionId: string): ResultRow[] {
    const [rows] = useQuery(
      queries.mealPlanningAgent.memberContextResults({ mealPlanningSessionId }),
    );
    return rows;
  }

  export function useCreateSession() {
    const z = useZero();
    return (params: { mealPlanningSessionId: string; userId: string }) => {
      const args = AgentSession.createArgs(MealPlanningMemberContextAgent.definition, {
        params: { ...params, initialMessageId: createId() },
      });
      return z.mutate(mutators.agentSession.create(args));
    };
  }

  export function deriveReadiness(args: {
    dateStart: Temporal.PlainDate;
    dateEnd: Temporal.PlainDate;
    sessionScope: Scope.Info<"user" | "space">;
    programs: readonly NutritionProgram.Preview[];
  }): Readiness {
    if (args.programs.length === 0) {
      return { status: "missing-nutrition-program", reason: "no-program" };
    }

    const dayIds = new Set<string>();

    for (
      let date = args.dateStart;
      Temporal.PlainDate.compare(date, args.dateEnd) <= 0;
      date = date.add({ days: 1 })
    ) {
      const day = NutritionProgram.dayForDate([...args.programs], date);
      if (!day) {
        return { status: "missing-nutrition-program", reason: "uncovered-date" };
      }
      dayIds.add(day.id);
    }

    if (dayIds.size !== 1) {
      return { status: "missing-nutrition-program", reason: "mixed-program-days" };
    }
    return { status: "ready" };
  }

  export function useReadiness(): ReadinessState {
    const { userId, dateStart, dateEnd, sessionScope } = useContext();
    const [programs, details] = useQuery(
      queries.nutritionProgram.inDateRange({
        userId,
        dateRange: { dateStart, dateEnd },
      }),
    );

    const readiness = useMemo<ReadinessState>(() => {
      if (details.type === "unknown") {
        return { status: "loading" };
      }
      return deriveReadiness({
        dateStart,
        dateEnd,
        sessionScope,
        programs,
      });
    }, [dateStart, dateEnd, details.type, programs, sessionScope]);

    return readiness;
  }

  export function useProgramPrerequisite(args: {
    userId: string;
    dateStart: Temporal.PlainDate;
    dateEnd: Temporal.PlainDate;
    lifecycle: PrerequisiteLifecycle;
  }) {
    const z = useZero();
    const readiness = useReadiness();
    const [continueWithoutTargets, setContinueWithoutTargets] = useState(false);
    const [programWizardOpen, setProgramWizardOpen] = useState(false);

    useEffect(() => {
      if (readiness.status !== "missing-nutrition-program") return;
      NutritionProgramWizard.preload(z, { type: "user", userId: args.userId });
    }, [args.userId, readiness.status, z]);

    return {
      readiness,
      shouldBlockStart:
        args.lifecycle.type === "member-context" &&
        args.lifecycle.mode === "not-started" &&
        readiness.status === "loading" &&
        !continueWithoutTargets,
      shouldShow:
        args.lifecycle.type === "member-context" &&
        args.lifecycle.mode === "not-started" &&
        readiness.status === "missing-nutrition-program" &&
        !continueWithoutTargets,
      programWizardOpen,
      openProgramWizard: () => setProgramWizardOpen(true),
      closeProgramWizard: () => setProgramWizardOpen(false),
      continueWithoutTargets: () => setContinueWithoutTargets(true),
    };
  }

  export function getProgramPrerequisiteDescription(readiness: ReadinessState): string {
    if (readiness.status !== "missing-nutrition-program") {
      return "Create a nutrition program before starting preferences so the assistant can use your calorie and macro targets for this session.";
    }
    switch (readiness.reason) {
      case "no-program":
        return "No nutrition program covers these session dates. Create one so the assistant can use calorie and macro targets.";
      case "uncovered-date":
        return "Your nutrition program does not cover every date in this session. Update targets before starting for target-aware planning.";
      case "mixed-program-days":
        return "These dates use different nutrition-program day targets. Use a consistent target setup before starting for target-aware planning.";
    }
  }

  export function getStartAvailability(args: {
    actorUserId: string;
    session: Pick<MealPlanningSession.Info, "id" | "group"> | null | undefined;
    plannerSessionExists: boolean;
    memberContextSessionExists: boolean;
  }): ActionAvailability<StartUnavailableReason> {
    const reason = MealPlanningMemberContextAgent.getCreateUnavailableReason({
      actorUserId: args.actorUserId,
      paramsUserId: args.actorUserId,
      session: args.session,
      plannerSessionExists: args.plannerSessionExists,
      memberContextSessionExists: args.memberContextSessionExists,
    });
    return reason ? ActionAvailability.unavailable(reason) : ActionAvailability.available();
  }

  export function getEditAvailability(args: {
    actorUserId: string;
    linkedSessionUserId: string | null | undefined;
    session: Pick<MealPlanningSession.Info, "id" | "group"> | null | undefined;
    plannerSessionExists: boolean;
  }): ActionAvailability<EditUnavailableReason> {
    const reason = MealPlanningMemberContextAgent.getEditUnavailableReason(args);
    return reason ? ActionAvailability.unavailable(reason) : ActionAvailability.available();
  }

  export function deriveReadinessSummary(
    session: Pick<MealPlanningSession.Info, "group" | "memberContexts"> | undefined,
    results: readonly Pick<ResultRow, "userId">[],
  ): ReadinessSummary {
    const resultUserIds = new Set(results.map((row) => row.userId));
    const contextsByUserId = new Map(
      session?.memberContexts.map((context) => [context.userId, context.data]) ?? [],
    );
    const rows =
      session?.group?.members.map((member) => {
        const result = contextsByUserId.get(member.userId);
        const status: Status =
          result !== undefined
            ? "complete"
            : resultUserIds.has(member.userId)
              ? "in-progress"
              : "not-started";
        return {
          userId: member.userId,
          label: member.profile?.name ?? member.userId,
          status,
          result: result ?? null,
        };
      }) ?? [];
    const incompleteMemberNames = rows
      .filter((row) => row.status !== "complete")
      .map((row) => row.label);
    return {
      total: rows.length,
      notStarted: rows.filter((row) => row.status === "not-started").length,
      inProgress: rows.filter((row) => row.status === "in-progress").length,
      complete: rows.filter((row) => row.status === "complete").length,
      incompleteMemberNames,
      rows,
    };
  }

  export function isSaveMemberContextPart(
    part: AgentTools.DeferredTool,
  ): part is AgentTools.DeferredTool & {
    type: "tool-saveMemberContext";
    toolCallId: string;
    input?: MealPlanningMemberContextAgent.ToolResult;
    output?: MealPlanningMemberContextAgent.ToolResult;
  } {
    return part.type === "tool-saveMemberContext";
  }

  export function isSaveMemberContextCompletedPart(part: AgentTools.DeferredTool): boolean {
    return isSaveMemberContextPart(part) && "output" in part;
  }

  export function isAskQuestionsPart(part: { type: string }): part is AskQuestionsPart {
    return AskQuestions.isPart(part);
  }

  export function isAskQuestionsCompletedPart(part: {
    type: string;
    state?: string;
  }): part is AskQuestionsCompletedPart {
    return AskQuestions.isCompletedPart(part);
  }

  export function formatPreferences(
    result:
      | MealPlanningMemberContextAgent.Result
      | MealPlanningMemberContextAgent.ToolResult
      | null
      | undefined,
  ): string {
    const preferences = Array.isArray(result?.preferences) ? result.preferences : [];
    return preferences.length ? preferences.join(", ") : "No saved preferences";
  }

  export function formatSummary(
    result:
      | MealPlanningMemberContextAgent.Result
      | MealPlanningMemberContextAgent.ToolResult
      | null
      | undefined,
  ): string {
    const parts = [formatPreferences(result)];
    if (result && "nutritionGoal" in result && result.nutritionGoal) {
      parts.push(formatNutritionGoal(result.nutritionGoal));
    }
    if (Array.isArray(result?.slotTargets)) {
      const included = result.slotTargets.filter((target) => target.status === "included").length;
      const skipped = result.slotTargets.length - included;
      parts.push(`${included} slot targets included${skipped ? `, ${skipped} skipped` : ""}`);
    }
    return parts.join("; ");
  }

  function formatNutritionGoal(goal: MealPlanningMemberContextAgent.NutritionGoal): string {
    if (goal.type === "maintenance") return "maintenance goal";
    const amount = Math.abs(goal.energyOffsetKcal);
    return goal.type === "weight_loss"
      ? `weight loss goal with ${amount} kcal deficit`
      : `muscle gain goal with ${amount} kcal surplus`;
  }
}
